import { useCallback } from 'react';
import { prop, pick } from 'ramda';
import { useLocation } from 'react-router-dom';
import { useNavigate } from '@theme/js/hooks/navigate';
import { getCleanUrl } from '@theme/js/utils/url.util';
import { languageService } from '@theme/js/services/language.service';
import { useAppSelector, usePageSelector } from '@core/js/store/hooks';
import { useClearIdsInPath } from '@core/js/components/system/Page/hocs/withCommonPage/hooks';
import { TAppState } from '@core/js/components/system/App/store/types';

export const useSetLanguagePathname = ({ shouldReload = false } = {}) => {
    const rootPage = useAppSelector(prop('rootPage'));
    const { dynamicRoutingUrls, defaultLocale }: {
        dynamicRoutingUrls?: [string]
        defaultLocale: TAppState['defaultLocale']
    } = useAppSelector(pick(['dynamicRoutingUrls', 'defaultLocale']));
    const navigateWithReplace = useNavigate({ replace: true, shouldReload }); /* Lugano changes */
    const pageUrl = usePageSelector(prop('url'));
    const { pathname } = useLocation();
    const getCleanDynamicUrl = useClearIdsInPath();

    /* Lugano changes */
    return useCallback((
        langToSet: string,
        normalizeUrl?: boolean,
        isLocaleUrl?: boolean
    ): void => {
        const pathLangAlias = languageService.getAliasForLanguage(langToSet);
        const pathLang = pathLangAlias === langToSet ? languageService.getShortLanguage(langToSet) : pathLangAlias;

        if (!pathLang) {
            return;
        }

        const cleanUrlPathname = getCleanUrl(pathname);
        const cleanUrl = isLocaleUrl
            ? getCleanUrl(pageUrl[langToSet] === rootPage ? '' : pageUrl[langToSet])
            : getCleanUrl();

        const cleanDynamicUrl = getCleanDynamicUrl(pathname);
        if (dynamicRoutingUrls && dynamicRoutingUrls?.some((item) => item === cleanDynamicUrl)) {
            const newPage = pageUrl[langToSet] || pageUrl[defaultLocale];
            const gameInfoUrl = cleanUrlPathname.replace(cleanDynamicUrl, newPage);
            navigateWithReplace(`/${pathLang}${gameInfoUrl}`);
        } else {
            navigateWithReplace(`/${pathLang}${normalizeUrl ? cleanUrl : '/'}`);
        }
    }, [navigateWithReplace, pageUrl, pathname, rootPage, defaultLocale, dynamicRoutingUrls, getCleanDynamicUrl]);
    /* Lugano changes */
};
